/**
 * When the viewer last opened each workspace, on this device. The membership
 * row's `last_online_at` is stamped per person, not per workspace, so
 * recency has to be kept where the opening happens: the browser. A device-
 * local answer is the right one for a picker anyway — "recent" means recent
 * here.
 *
 * Stored as one JSON object, workspace id → ISO timestamp. Every read and
 * write is guarded: storage can be missing, full, or blocked, and the picker
 * must render (alphabetically) without it.
 */
const STORAGE_KEY = "workspace_last_opened";
const MAX_ENTRIES = 50;

export type WorkspaceRecents = Readonly<Record<string, string>>;

export function readWorkspaceRecents(): WorkspaceRecents {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: Record<string, string> = {};
    for (const [id, at] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof at === "string") out[id] = at;
    }
    return out;
  } catch {
    return {};
  }
}

export function markWorkspaceOpened(workspaceId: string, now = new Date()): void {
  try {
    const next: Record<string, string> = {
      ...readWorkspaceRecents(),
      [workspaceId]: now.toISOString(),
    };
    // Bound the map so a long-lived device never grows it past what a picker
    // could show: keep the newest entries.
    const trimmed = Object.entries(next)
      .sort(([, a], [, b]) => b.localeCompare(a))
      .slice(0, MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(trimmed)));
  } catch {
    // Storage unavailable: recency simply stays unknown on this device.
  }
}
